// Bridge minimale verso il runtime Capacitor iniettato dalla shell nativa iOS.
// Il VTO è una sotto-pagina della stessa webview: window.Capacitor esiste
// (iniettato via WKUserScript su ogni navigazione) ma non prendiamo alcuna
// dipendenza compile-time — su web/PWA tutto qui dentro è un no-op.

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, Record<string, (...args: unknown[]) => Promise<unknown>> | undefined>;
};

function cap(): CapacitorGlobal | null {
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor ?? null;
}

export function isNativeIos(): boolean {
  const c = cap();
  return !!(c && c.isNativePlatform?.() && c.getPlatform?.() === 'ios');
}

export function openExternal(url: string): void {
  const browser = cap()?.Plugins?.Browser;
  if (browser && typeof browser.open === 'function') {
    void browser.open({ url });
  } else {
    window.open(url, '_blank');
  }
}

// Interceptor capture-phase: nella shell nativa un <a target="_blank"> verso un
// sito esterno caricherebbe DENTRO la webview (niente chrome di navigazione,
// niente back) — i buy link affiliati sarebbero un vicolo cieco. Qui li
// dirottiamo sul browser di sistema. Il preventDefault non blocca i tracker in
// bubble-phase (Umami) perché preventDefault ≠ stopPropagation.
export function installExternalLinkInterceptor(): void {
  if (!isNativeIos()) return;
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as Element | null;
      const a = target?.closest?.('a[href]');
      if (!a) return;
      const href = a.getAttribute('href') ?? '';
      if (!/^https?:\/\//i.test(href)) return;
      e.preventDefault();
      openExternal(href);
    },
    true
  );
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

// Share nativo: WKWebView non espone navigator.share con file e non ha un
// download delegate (<a download> muore in silenzio). Scriviamo il JPEG nella
// cache dell'app e lo passiamo alla share sheet iOS (che include "Salva
// immagine"). Ritorna null quando i plugin non sono disponibili, così il
// chiamante usa i path web.
export async function shareBlobNative(
  blob: Blob,
  filename: string,
  title: string,
  text: string
): Promise<'shared' | 'cancelled' | null> {
  const plugins = cap()?.Plugins;
  const fs = plugins?.Filesystem;
  const share = plugins?.Share;
  if (!isNativeIos() || !fs || typeof fs.writeFile !== 'function' || !share || typeof share.share !== 'function') {
    return null;
  }
  try {
    const data = await blobToBase64(blob);
    const written = (await fs.writeFile({
      path: filename,
      data,
      directory: 'CACHE'
    })) as { uri?: string };
    if (!written?.uri) return null;
    await share.share({ title, text, files: [written.uri] });
    return 'shared';
  } catch (err) {
    const msg = String((err as Error)?.message ?? err).toLowerCase();
    if (msg.includes('cancel')) return 'cancelled';
    // Qualsiasi altro errore → lascia provare i fallback web.
    return null;
  }
}
